import {useMemo} from 'react';
import type {TaskTypeEnum} from 'src/api/ProactApiClient';
import type {BreadcrumbItem} from 'src/app/layout/BackBar';
import {TaskPageTitleDict} from 'src/app/tasks/taskHelpers';
import {PathUtility} from 'src/shared/constants/PathBuilder';

export type TaskBreadcrumbMode =
  | 'view'
  | 'create'
  | 'edit'
  | 'clone'
  | 'user'
  | 'supervisor-approval';

export interface UseTaskBreadcrumbsProps {
  taskType: TaskTypeEnum;
  mode: TaskBreadcrumbMode;
  taskId?: string;
  itemSubject?: string;
}

export const useTaskBreadcrumbs = ({
  taskType,
  mode,
  taskId,
  itemSubject,
}: UseTaskBreadcrumbsProps): BreadcrumbItem[] => {
  return useMemo(() => {
    const crumbs: BreadcrumbItem[] = [];

    switch (mode) {
      case 'view': {
        const managePath = PathUtility.TasksDashboard(taskType, 'manage');
        crumbs.push({
          label: `Manage ${TaskPageTitleDict[taskType].plural}`,
          to: managePath,
        });
        crumbs.push({label: itemSubject || 'View'});
        break;
      }

      case 'create': {
        const managePath = PathUtility.TasksDashboard(taskType, 'manage');
        crumbs.push({
          label: `Manage ${TaskPageTitleDict[taskType].plural}`,
          to: managePath,
        });
        crumbs.push({label: 'Create'});
        break;
      }

      case 'edit': {
        const managePath = PathUtility.TasksDashboard(taskType, 'manage');
        const viewPath = PathUtility.TaskView(taskType, taskId ?? '', 'content');
        crumbs.push({
          label: `Manage ${TaskPageTitleDict[taskType].plural}`,
          to: managePath,
        });
        crumbs.push({label: itemSubject || 'Item', to: viewPath});
        crumbs.push({label: 'Edit'});
        break;
      }

      case 'clone': {
        const managePath = PathUtility.TasksDashboard(taskType, 'manage');
        const viewPath = PathUtility.TaskView(taskType, taskId ?? '', 'content');
        crumbs.push({
          label: `Manage ${TaskPageTitleDict[taskType].plural}`,
          to: managePath,
        });
        crumbs.push({label: itemSubject || 'Item', to: viewPath});
        crumbs.push({label: 'Clone'});
        break;
      }

      case 'user': {
        const dashboardPath = PathUtility.TasksDashboard(taskType, 'user');
        crumbs.push({
          label: `My ${TaskPageTitleDict[taskType].plural}`,
          to: dashboardPath,
        });
        crumbs.push({label: itemSubject || 'View'});
        break;
      }

      case 'supervisor-approval': {
        const dashboardPath = PathUtility.TasksDashboard(
          taskType,
          'supervisor-approval'
        );
        crumbs.push({
          label: `${TaskPageTitleDict[taskType].plural} - Supervisor Approval`,
          to: dashboardPath,
        });
        crumbs.push({label: 'View'});
        break;
      }
    }

    return crumbs;
  }, [taskType, mode, taskId, itemSubject]);
};
